// neutral tier: app-specific, theme-agnostic component. Extends the base
// component directly (5 dirs up to the webapp root, then into core/themes/base),
// never a leaf theme's override — neutral cannot assume any particular leaf theme
// is in the chain.
const component = require('../../../../../core/themes/base/components/component/component');

// Coordinator + selection bar for the bundle product page's optionals-selection tab.
//
// The cap: it keeps the TOTAL number of optional products picked across every
// optionalProductCard's dialer at or below the bundle's remaining optional slots
// (this.limit): once the total reaches the limit it blocks any further increase on
// every dialer — intercepting the + click in the capture phase, before dialer.js
// applies it — and shows a brief "limit reached" toast. A hand-typed value that
// would exceed the limit is clamped back. The API re-validates the cap on save;
// this is UX only.
//
// The bar: the package being assembled, as chips. The locked ones (the bundle's
// included products and the optionals saved in a previous visit) are rendered
// visible by the template and never touched here; the selectable ones are rendered
// hidden, one per bundleProductId, and this JS reveals/hides them, writes their ×N
// quantity, keeps the "N of M selected" counter in sync, flags the bar complete and
// disables the save action while nothing is picked. Chips are never built from
// strings — the server rendered them all.
//
// The governed dialers are resolved by a shared extra attribute (groupAttribute /
// groupValue, set identically on this gatekeeper and on every optional dialer),
// via hm.app.componentsWithExtra — no name or DOM-structure coupling. Each dialer
// additionally carries its bundleProductId extra, which is what pairs it with its
// chip.
const bundleOptionalsGatekeeper = Object.assign({}, component, {
    limit: 0,                                  // data-zit-limit: order-wide remaining optional slots
    message: 'optionalProductsLimitReached',   // data-zit-message: dictionary key of the toast
    groupAttribute: '',                        // data-zit-group-attribute: the shared extra attribute name
    groupValue: null,                          // data-zit-group-value: the shared extra attribute value
    counterText: '',                           // data-zit-counter-text: the counter sentence, with [selected] / [limit]
    subelements: {
        slider: null,
        counter: null,
        saveAction: null,
    },
    saveActionComponent: null,
    scopeRoot: null,
    clamping: false,
    notifyTimer: false,

    init: function () {
        component.init.call(this);

        // Scope click/change interception to the owning form — every optional dialer
        // submits within it — so the coordinator never intercepts events elsewhere.
        this.scopeRoot = this.element.closest('form') || document;

        this.wire();
    },

    // Runs after every component on the page has been initialised, so the dialers and
    // the save action are registered and resolvable. init() is too early for both.
    inited: function () {
        component.inited.call(this);

        this.resolveSaveAction();
        this.syncChips();
    },

    // The save action instance behind the bar's button, looked up through its
    // wrapper subelement rather than by a hardcoded name.
    resolveSaveAction: function () {
        if (!this.hasSubelement('saveAction')) {
            return;
        }

        let actionElement = this.subelements.saveAction.querySelector('[data-zit-component]');

        if (actionElement !== null && typeof this.hm.app.components[actionElement.id] !== typeof undefined) {
            this.saveActionComponent = this.hm.app.components[actionElement.id];
        }
    },

    // The optional-product dialer component INSTANCES this gatekeeper governs,
    // found by the shared extra attribute (resolved at call time, so all dialers
    // are already initialised).
    dialers: function () {
        return this.hm.app.componentsWithExtra(this.groupAttribute, this.groupValue);
    },

    dialerValue: function (dialer) {
        let value = parseInt(dialer.getElementValue(), 10);
        return isNaN(value) ? 0 : value;
    },

    currentTotal: function () {
        return this.dialers().reduce((sum, dialer) => sum + this.dialerValue(dialer), 0);
    },

    // Map an event target (a button/input inside a dialer) to the governed dialer
    // instance it belongs to, or false if it is not one of ours.
    dialerFor: function (target) {
        let dialerEl = target.closest('[data-zit-component="dialer"]');
        if (!dialerEl) {
            return false;
        }
        return this.dialers().find((dialer) => dialer.element === dialerEl) || false;
    },

    // The governed dialer that posts the given bundle product, or false — the chip
    // side of the same pairing (each dialer carries its bundleProductId extra).
    dialerForBundleProduct: function (bundleProductId) {
        let dialers = this.dialers();
        return dialers.find((dialer) => String(dialer.extraAttributes.bundleProductId) === String(bundleProductId)) || false;
    },

    wire: function () {
        // Block an increment the instant the limit is reached, before dialer.js's own
        // click handler runs: a capture-phase listener + stopImmediatePropagation keeps
        // the event from ever reaching the button's own handler.
        this.scopeRoot.addEventListener('click', (e) => {
            let incrementButton = e.target.closest('[data-zit-subelement-type="incrementButton"]');
            if (!incrementButton || !this.dialerFor(incrementButton)) {
                return;
            }
            if (this.currentTotal() >= this.limit) {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.notifyLimit();
            }
        }, true);

        // Every quantity change — a dialer button, a hand-typed value, or a chip
        // removal — lands here: clamp an over-limit total back down, then refresh the
        // bar. Guarded against the re-entrant zitDialerChange the correction dispatches.
        this.scopeRoot.addEventListener('zitDialerChange', (e) => {
            if (this.clamping) {
                return;
            }

            this.enforceLimit(e.target);
            this.syncChips();
        });

        // A chip's remove button clears its dialer completely; the resulting
        // zitDialerChange refreshes the bar through the single path above.
        if (this.hasSubelement('slider')) {
            this.subelements.slider.addEventListener('click', (e) => {
                let removeButton = e.target.closest('[data-zit-chip-remove]');
                if (!removeButton) {
                    return;
                }

                e.preventDefault();

                let dialer = this.dialerForBundleProduct(removeButton.dataset.zitChipRemove);

                if (dialer !== false) {
                    dialer.setCount(0);
                }
            });
        }
    },

    // Safety net for hand-typed values: if a change pushed the total over the limit,
    // pull the offending dialer back down and warn.
    enforceLimit: function (target) {
        let overBy = this.currentTotal() - this.limit;

        if (overBy <= 0) {
            return;
        }

        let dialer = this.dialerFor(target);

        if (!dialer) {
            return;
        }

        let corrected = Math.max(0, this.dialerValue(dialer) - overBy);

        this.clamping = true;
        dialer.setCount(corrected);
        this.clamping = false;

        this.notifyLimit();
    },

    // Reflect the current dialer values in the bar: one visible chip per optional
    // being picked (with its ×N when more than one), the counter sentence, the
    // complete flag and the save action's enabled state.
    syncChips: function () {
        let total = 0;
        let dialers = this.dialers();

        dialers.forEach((dialer) => {
            let value = this.dialerValue(dialer);
            total += value;

            let bundleProductId = dialer.extraAttributes.bundleProductId;

            if (typeof bundleProductId === typeof undefined) {
                return;
            }

            this.syncChip(bundleProductId, value);
        });

        this.syncCounter(total);
    },

    // One chip: shown only while its dialer holds a positive quantity. Hidden with
    // helper.hideElement (the theme's displayNone class) rather than filterHide,
    // which would mark the chip notSubmittable and fire the window-visibility events.
    syncChip: function (bundleProductId, value) {
        if (!this.hasSubelement('slider')) {
            return;
        }

        let chip = this.subelements.slider.querySelector('[data-zit-optional-chip="' + bundleProductId + '"]');

        if (chip === null) {
            return;
        }

        this.hm.helper.showElement(chip, value > 0);

        let quantity = chip.querySelector('[data-zit-chip-quantity]');

        if (quantity !== null) {
            // A single unit needs no multiplier — the chip itself already says "one".
            quantity.textContent = value > 1 ? '×' + value : '';
        }
    },

    syncCounter: function (total) {
        if (this.hasSubelement('counter')) {
            this.subelements.counter.textContent = this.counterText
                .replace('[selected]', total)
                .replace('[limit]', this.limit);
        }

        // The theme styles the finished selection off this flag (and may highlight the
        // counter), the same way the dialer flags its active state.
        this.element.dataset.zitOptionalsComplete = (total >= this.limit) ? 'true' : 'false';

        // Nothing picked means nothing to save.
        if (this.saveActionComponent !== null) {
            this.saveActionComponent.disable(total === 0);
        }
    },

    // Brief, throttled warning toast (rapid clicks don't stack the message).
    notifyLimit: function () {
        if (this.notifyTimer) {
            return;
        }
        this.hm.msgs.show({ type: 'warning', text: this.message });
        this.notifyTimer = setTimeout(() => { this.notifyTimer = false; }, 2500);
    }
});

module.exports = bundleOptionalsGatekeeper;
